import { FC, useCallback, useEffect, useRef, useState } from "react";
import { WithStyle } from "utils/WithStyle";
import {
    MaxRangeInput,
    MinRangeInput,
    RangeSelectorWrapper,
    Slider,
    SliderLeftValue,
    SliderRange,
    SliderRightValue,
    SliderTrack,
    ValueContainer,
} from "./RangeSelector.styled";
import { RangeSelectorProps } from "./types";

export const Component: FC<RangeSelectorProps> = ({
    min,
    max,
    minRange,
    maxRange,
    valueUnit,
    onChange,
    fullWidth,
    height = 6,
}) => {
    const [minVal, setMinVal] = useState(min);
    const [maxVal, setMaxVal] = useState(max);
    const minValRef = useRef<HTMLInputElement>(null);
    const maxValRef = useRef<HTMLInputElement>(null);
    const range = useRef<HTMLDivElement>(null);

    // Convert to percentage
    const getPercent = useCallback(
        (value: number) =>
            Math.round(((value - minRange) / (maxRange - minRange)) * 100),
        [minRange, maxRange]
    );

    // Set width of the range to decrease from the left side
    useEffect(() => {
        if (maxValRef.current) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(+maxValRef.current.value);
            // Precede with '+' to convert the value from type string to type number

            if (range.current) {
                range.current.style.left = `${minPercent}%`;
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [minVal, getPercent]);

    // Set width of the range to decrease from the right side
    useEffect(() => {
        if (minValRef.current) {
            const minPercent = getPercent(+minValRef.current.value);
            const maxPercent = getPercent(maxVal);

            if (range.current) {
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [maxVal, getPercent]);

    // Get min and max values when their state changes
    useEffect(() => {
        onChange && onChange(minVal, maxVal);
    }, [minVal, maxVal, onChange]);

    useEffect(() => {
        if (min === minRange && max === maxRange) {
            setMinVal(min);
            setMaxVal(max);
        }
    }, [min, max, minRange, maxRange]);

    return (
        <RangeSelectorWrapper fullWidth={fullWidth} sliderHeight={height}>
            <ValueContainer>
                <SliderLeftValue variant="label">
                    {minVal} {valueUnit}
                </SliderLeftValue>
                <SliderRightValue variant="label">
                    {maxVal} {valueUnit}
                </SliderRightValue>
            </ValueContainer>
            <Slider>
                <MinRangeInput
                    type="range"
                    min={minRange}
                    max={maxRange}
                    value={minVal}
                    ref={minValRef}
                    bringToFront={maxVal > maxRange - 100}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = Math.min(+event.target.value, maxVal - 1);
                        setMinVal(value);
                        event.target.value = value.toString();
                    }}
                />
                <MaxRangeInput
                    type="range"
                    min={minRange}
                    max={maxRange}
                    value={maxVal}
                    ref={maxValRef}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = Math.max(+event.target.value, minVal + 1);
                        setMaxVal(value);
                        event.target.value = value.toString();
                    }}
                />
                <SliderTrack></SliderTrack>
                <SliderRange ref={range}></SliderRange>
            </Slider>
        </RangeSelectorWrapper>
    );
};

export const RangeSelector: FC<RangeSelectorProps> & WithStyle = Object.assign(
    Component,
    {
        Style: RangeSelectorWrapper,
    }
);
